/* eslint-disable react/prop-types */
import React from "react";
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  HStack,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useMutation, useQuery } from "@apollo/client";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";

import WithAppLayout from "../../../components/WithAppLayout";
import { LoadingIndicator } from "../../../components/LoadingIndicator";
import {
  JOB_CREATE_PAGE_QUERY,
  JOB_PREVIEW_QUERY,
  UPDATE_JOB_MUTATION,
  UPDATE_MOBLYZE_JOB_STATUS_MUTATION,
  UPLOAD_JOB_COVER_IMAGE_MUTATION,
} from "../../../graphql/jobs_admin";
import { BULLHORN_JOB_IMPORT_STATUS_QUERY } from "../../../graphql/jobs";
import {
  JOB_DETAILS_PATH,
  PENDING_JOBS_PATH,
  SCREENING_CRITERIA_PATH,
} from "../../../routes";
import { applyContactFieldsForAgencyType } from "../../../helpers/jobContactFields";
import { normalizeBulletDescription } from "../../../helpers/jobListingDescription";
import { ownerUserIdVariables } from "../../../helpers/jobOwner";
import {
  draftFromJob,
  missingRequiredFields,
} from "../../../helpers/jobCreateFlowHelpers";
import {
  COVER_IMAGES,
  PreviewCard,
  REQUIRED_BEFORE_PUBLISH_GLOW_PROPS,
  urlToFile,
} from "../../../components/JobPreviewCard";
import { EditModal } from "../../../components/JobPreviewEditModal";

// Statuses a reviewer can publish from. Draft is included: it is not live
// either, and this page is the natural place to finish and approve one.
const PUBLISHABLE_STATUSES = new Set(["Pending", "Draft"]);

const STATUS_NOTES = {
  Published: "This job is already live.",
  Closed: "This job has been closed.",
  Deleted: "This job has been deleted.",
};

// Variables for updateJobDetails, mirroring what the create flow sends to
// createJob. Roles, skills and certifications are left alone: the API only
// overwrites them when given a non-empty list, so omitting them keeps what
// the job already has. ownerUserId is sent only when the reviewer picked a
// different owner from the job's current one (currentOwnerId); left out, the
// API keeps the owner.
export function publishVariablesFromDraft(jobId, draft, agencyType, currentOwnerId = null) {
  const publishDraft = applyContactFieldsForAgencyType(
    {
      ...draft,
      requirementsDescription: draft.requirementsDescription
        ? normalizeBulletDescription(draft.requirementsDescription)
        : draft.requirementsDescription,
      responsibilitiesDescription: draft.responsibilitiesDescription
        ? normalizeBulletDescription(draft.responsibilitiesDescription)
        : draft.responsibilitiesDescription,
    },
    agencyType
  );
  return {
    moblyzeJobId: jobId,
    bullhornId: publishDraft.bullhornId || null,
    category: publishDraft.category || null,
    // Imported jobs may have no title yet; the API treats a missing title as a
    // blocking issue, so the reviewed job title fills it.
    title: publishDraft.category || null,
    employmentType: publishDraft.employmentType,
    numOpenings: publishDraft.numOpenings,
    clientOrganizationId: publishDraft.clientOrganizationId,
    employerContactName: publishDraft.employerContactName,
    employerContactEmail: publishDraft.employerContactEmail,
    employerContactPhone: publishDraft.employerContactPhone,
    agencyContactName: publishDraft.agencyContactName,
    agencyContactEmail: publishDraft.agencyContactEmail,
    agencyContactPhone: publishDraft.agencyContactPhone,
    location: publishDraft.location,
    duration: publishDraft.duration,
    startDate: publishDraft.isAsap ? null : publishDraft.startDate,
    isAsap: publishDraft.isAsap || false,
    requirementsDescription: publishDraft.requirementsDescription,
    responsibilitiesDescription: publishDraft.responsibilitiesDescription,
    vesselId: publishDraft.vesselId,
    candidateWorkLocationIds: publishDraft.candidateWorkLocationIds || [],
    payRateValue: publishDraft.payRateValue,
    payFrequency: publishDraft.payFrequency,
    payCurrency: publishDraft.payCurrency,
    ...ownerUserIdVariables(publishDraft.owner, currentOwnerId),
  };
}

// What the auto-import judge decided about a Bullhorn job order, in the
// recruiter's terms. Rendered on the Review job page so the person approving a
// Pending job sees WHY the pipeline held it, not just which fields are empty.
export function importVerdictNote(status) {
  if (!status?.judgeVerdict) return null;
  if (status.judgeVerdict === "NeedsReview") {
    return {
      tone: "purple",
      title: "Held for your review by the auto-import judge",
      body: status.judgeReasoning || "The judge did not give a reason.",
      extra:
        status.missingRequiredFields?.length > 0
          ? `It could not fill: ${status.missingRequiredFields.join(", ")}.`
          : null,
    };
  }
  if (status.judgeVerdict === "Publish") {
    return {
      tone: "green",
      title:
        status.gateFailed === "publish_daily_cap"
          ? "Approved to publish automatically; waiting for today's publish cap"
          : "Approved to publish automatically",
      body:
        status.gateFailed === "publish_daily_cap"
          ? "It will go live on its own when the cap allows. You can approve it now to skip the wait."
          : status.judgeReasoning || null,
      extra: null,
    };
  }
  return null;
}

function ImportVerdictNote({ status }) {
  const note = importVerdictNote(status);
  if (!note) return null;
  return (
    <Box mb={4} p={3} bg={`${note.tone}.900`} borderRadius="md" data-testid="import-verdict">
      <Text fontSize="sm" color={`${note.tone}.100`} fontWeight="semibold">
        {note.title}
      </Text>
      {note.body && (
        <Text fontSize="sm" color={`${note.tone}.100`} mt={1}>
          {note.body}
        </Text>
      )}
      {note.extra && (
        <Text fontSize="sm" color={`${note.tone}.200`} mt={1}>
          {note.extra}
        </Text>
      )}
    </Box>
  );
}

function JobPreviewPage({ currentUser }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const agencyType = currentUser?.agency?.type ?? "";

  const [draft, setDraft] = React.useState(null);
  const [editField, setEditField] = React.useState(null);
  const [publishing, setPublishing] = React.useState(false);

  const { data: jobData, loading, error } = useQuery(JOB_PREVIEW_QUERY, {
    variables: { id },
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      if (data?.moblyzeJob) {
        setDraft(applyContactFieldsForAgencyType(draftFromJob(data.moblyzeJob), agencyType));
      }
    },
  });
  const job = jobData?.moblyzeJob;

  // Bullhorn-sourced Pending jobs carry the judge's decision; ask for it.
  const bullhornOrderId = /^\d+$/.test(job?.bullhornId || "") ? job.bullhornId : null;
  const { data: importData } = useQuery(BULLHORN_JOB_IMPORT_STATUS_QUERY, {
    variables: { bullhornId: bullhornOrderId },
    skip: !bullhornOrderId || job?.status !== "Pending",
  });
  const importStatus = importData?.bullhornJobImportStatus;

  const { data: pageData } = useQuery(JOB_CREATE_PAGE_QUERY);
  const clientOrgs = pageData?.getClientOrganizations || [];
  const vessels = pageData?.getVessels || [];
  const workLocationNameById = React.useMemo(() => {
    const map = {};
    (pageData?.candidateWorkLocations || []).forEach((loc) => {
      map[loc.id] = loc.name;
    });
    return map;
  }, [pageData]);
  const workLocationOptions = React.useMemo(
    () =>
      (pageData?.candidateWorkLocations || []).map((loc) => ({
        value: loc.id,
        label: loc.name,
      })),
    [pageData]
  );

  const [updateJobDetails] = useMutation(UPDATE_JOB_MUTATION, { fetchPolicy: "no-cache" });
  const [uploadCoverImage] = useMutation(UPLOAD_JOB_COVER_IMAGE_MUTATION, {
    fetchPolicy: "no-cache",
  });
  const [updateJobStatus] = useMutation(UPDATE_MOBLYZE_JOB_STATUS_MUTATION, {
    fetchPolicy: "no-cache",
  });

  const handlePublish = async () => {
    const missing = missingRequiredFields(draft);
    if (missing.length) {
      toast({
        title: "Complete required fields",
        description: `Missing: ${missing.join(", ")}`,
        status: "warning",
      });
      return;
    }
    setPublishing(true);
    try {
      await updateJobDetails({
        variables: publishVariablesFromDraft(id, draft, agencyType, job?.owner?.id),
      });

      // Only re-upload when the reviewer swapped the cover for a predefined
      // image; an existing uploaded cover is left as is.
      if (!draft.coverImageUrl && draft.coverImageIndex != null) {
        try {
          const coverFile = await urlToFile(COVER_IMAGES[draft.coverImageIndex] || COVER_IMAGES[0]);
          await uploadCoverImage({ variables: { moblyzeJobId: id, imageFile: coverFile } });
        } catch (coverErr) {
          toast({
            title: "Cover image not updated",
            description:
              "The job was saved, but uploading the cover image failed. You can change it from the job editor. " +
              coverErr.message,
            status: "warning",
            duration: 12000,
            isClosable: true,
          });
        }
      }

      await updateJobStatus({ variables: { moblyzeJobId: id, status: "Published" } });
      toast({ title: "Job published", status: "success" });
      navigate(`${SCREENING_CRITERIA_PATH.replace(":id", id)}?new=true`);
    } catch (e) {
      toast({ title: "Publish failed", description: e.message, status: "error" });
    } finally {
      setPublishing(false);
    }
  };

  if (loading && !job) {
    return <LoadingIndicator />;
  }

  if (error) {
    return (
      <Container maxW="container.md" py={10}>
        <Text>Error loading job. Please try again.</Text>
        <Button mt={4} onClick={() => window.location.reload()}>
          Retry
        </Button>
      </Container>
    );
  }

  if (!job) {
    return (
      <Container maxW="container.md" py={10}>
        <Text>Job not found.</Text>
      </Container>
    );
  }

  const publishable = PUBLISHABLE_STATUSES.has(job.status);
  const missing = draft ? missingRequiredFields(draft) : [];
  const canPublish = publishable && draft && missing.length === 0 && !publishing;
  const detailsPath = JOB_DETAILS_PATH.replace(":id", job.id);

  return (
    <Container maxW="container.lg" py={6}>
      <Flex justify="space-between" align="center" mb={4} gap={4} wrap="wrap">
        <Box>
          <Heading size="lg">Review job</Heading>
          <HStack spacing={3} mt={1}>
            <Button as={RouterLink} to={PENDING_JOBS_PATH} variant="link" size="sm" color="blue.300">
              Back to pending jobs
            </Button>
            <Button as={RouterLink} to={detailsPath} variant="link" size="sm" color="blue.300">
              Job details
            </Button>
          </HStack>
        </Box>
        {publishable && (
          <Button
            colorScheme="green"
            size="lg"
            onClick={handlePublish}
            isDisabled={!canPublish}
            isLoading={publishing}
            loadingText="Publishing"
          >
            Approve & publish
          </Button>
        )}
      </Flex>

      <Box maxW="720px">
        {publishable ? (
          <Box mb={4} p={3} bg="blue.900" borderRadius="md">
            <Text fontSize="sm" color="blue.100" fontWeight="semibold">
              This job is not live yet. Review the listing below, fix anything that looks wrong,
              then approve it to publish.
            </Text>
            {job.bullhornId && (
              <Text fontSize="sm" color="blue.200" mt={1}>
                Bullhorn job order {job.bullhornId}
              </Text>
            )}
          </Box>
        ) : (
          <Box mb={4} p={3} bg="gray.700" borderRadius="md">
            <Text fontSize="sm" color="gray.100" fontWeight="semibold">
              {STATUS_NOTES[job.status] || `This job is ${job.status}.`}{" "}
              <Button as={RouterLink} to={detailsPath} variant="link" size="sm" color="blue.300">
                View job details
              </Button>
            </Text>
          </Box>
        )}

        {publishable && <ImportVerdictNote status={importStatus} />}

        {publishable && missing.length > 0 && (
          <Box mb={4} p={3} bg="orange.900" {...REQUIRED_BEFORE_PUBLISH_GLOW_PROPS}>
            <Text fontSize="sm" color="orange.100" fontWeight="semibold">
              Required before publish: {missing.join(", ")}
            </Text>
          </Box>
        )}

        {draft && (
          <PreviewCard
            draft={draft}
            vessels={vessels}
            clientOrgs={clientOrgs}
            workLocationNameById={workLocationNameById}
            agencyType={agencyType}
            onEdit={publishable ? setEditField : () => {}}
          />
        )}
      </Box>

      <EditModal
        field={editField}
        draft={draft}
        agencyType={agencyType}
        clientOrgs={clientOrgs}
        vessels={vessels}
        workLocationOptions={workLocationOptions}
        onClose={() => setEditField(null)}
        onSave={(patch) => {
          const next = { ...patch };
          if (typeof next.requirementsDescription === "string") {
            next.requirementsDescription = normalizeBulletDescription(next.requirementsDescription);
          }
          if (typeof next.responsibilitiesDescription === "string") {
            next.responsibilitiesDescription = normalizeBulletDescription(next.responsibilitiesDescription);
          }
          setDraft((d) => ({ ...d, ...next }));
          setEditField(null);
        }}
      />
    </Container>
  );
}

export { JobPreviewPage };
export default WithAppLayout(JobPreviewPage);
